import React from 'react';
import propTypes from 'prop-types';
import { Badge } from 'reactstrap';
import { Link } from 'react-router-dom';
import BadgeThin from '../../../components/Badges/BadgeThin';
import { FontAwesomeIcon, SimpleLineIcon } from '../../../components/Icons';
import Paragraph from '../../../styled/Typograph/Paragraph';
import SmallInline from '../../../styled/Typograph/Small/SmallInline';

const APP_FRONTEND_SOURCES = ['APP', 'ANDROID', 'IOS', 'MOBILE_APP'];
const CUSTOM_FRONTEND_FRANCHISES = ['kard_avenue'];
const CUSTOM_FRONTEND_CATEGORY = 'Trading Cards';

const normalizeValue = value => (value || '').toString().trim().toLowerCase();

const hasCustomFrontendIcon = franchise => CUSTOM_FRONTEND_FRANCHISES.includes(normalizeValue(franchise));
const hasCustomFrontendCategory = category => category === CUSTOM_FRONTEND_CATEGORY;

const isMobileAppFrontend = createdSource => APP_FRONTEND_SOURCES.includes((createdSource || '').toString().trim().toUpperCase());

const FrontendPurchaseIcon = ({ createdSource, franchise }) => {
  if (!createdSource) {
    return null;
  }

  if (!hasCustomFrontendIcon(franchise)) {
    return (
      <SimpleLineIcon
        className="mr-1"
        style={{ fontSize: '.75rem' }}
        iconType={isMobileAppFrontend(createdSource) ? 'screen-smartphone' : 'screen-desktop'}
      />
    );
  }

  const sharedStyle = {
    width: 18,
    height: 18,
    marginRight: 6,
    marginBottom: 2,
    verticalAlign: 'middle',
    color: '#27aae1',
    stroke: 'currentColor',
    fill: 'none',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };

  if (isMobileAppFrontend(createdSource)) {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" style={sharedStyle}>
        <rect x="4.5" y="2.5" width="10" height="19" rx="2.2" />
        <path d="M8.5 4.8h2.1" />
        <path d="M4.5 18.8h10" />
        <circle cx="9.5" cy="20.1" r=".7" fill="currentColor" stroke="none" />
        <g transform="translate(14.8 4.5) rotate(28 3.5 3.5)">
          <rect x="0.8" y="0.8" width="6.4" height="6.4" rx="0.9" />
          <path d="M2.9 5.5V2.5" />
          <path d="M2.9 4l2.2-1.5" />
          <path d="M2.9 4l2.2 1.5" />
        </g>
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" style={sharedStyle}>
      <rect x="3" y="5" width="12" height="9" rx="1.5" />
      <path d="M3 14.5h12" />
      <path d="M8 14.5l-.8 3h3.6l-.8-3" />
      <path d="M6.5 17.8h4.5" />
      <g transform="translate(13 2.3) rotate(28 3.5 3.5)">
        <rect x="0.8" y="0.8" width="6.4" height="6.4" rx="0.9" />
        <path d="M2.6 2.7v2.9" />
        <path d="M2.6 4.1l2.6-1.4" />
        <path d="M2.6 4.1l2.6 1.4" />
      </g>
    </svg>
  );
};

const OrderSummariesColumn = props => {
  const { id, category, invoiceNumber, userId, userEmail, size, displayName, SKU, quantity, colour, preOrder, paymentBankTransfer, paymentMethod, createdSource, franchise } = props;
  const shouldUseCustomFrontendIcon = hasCustomFrontendIcon(franchise) && hasCustomFrontendCategory(category);
  return (
    <Paragraph>
      { shouldUseCustomFrontendIcon
        ? <FrontendPurchaseIcon createdSource={createdSource} franchise={franchise} />
        : createdSource && (
          <SimpleLineIcon className="mr-1" style={{fontSize: '.75rem'}} iconType={isMobileAppFrontend(createdSource) ? 'screen-smartphone': 'screen-desktop'}/>
        )
      }
      { invoiceNumber } &nbsp;{preOrder?<Badge color="primary">PREORDER</Badge>:''}<br/>
      { displayName }<br/>
      <SmallInline>({ SKU })</SmallInline>
      <SmallInline>(Quantity: { quantity })</SmallInline>
      <SmallInline className="text-muted">{ colour }</SmallInline>
      <br/>
      {category && (
        <BadgeThin
          className="text-uppercase mr-1"
          color="primary"
        >
          <FontAwesomeIcon className="mr-1" iconType="tags"/>
          {category}
        </BadgeThin>
      )}
      <BadgeThin color="success" className="text-uppercase mr-1">SIZE: {size}</BadgeThin>
      {
        paymentMethod==='bank_transfer'&&paymentBankTransfer.length>0 && (
          <BadgeThin className="text-uppercase mr-1" color="success">
            <FontAwesomeIcon className="mr-1" iconType="exchange"/>
            {paymentBankTransfer[0].destination_bank? paymentBankTransfer[0].destination_bank.bank_name: 'undefined'}
          </BadgeThin>
        )
      }
      <BadgeThin>
        <FontAwesomeIcon className="mr-1" iconType="user"/>
        {userId? <Link style={{color: '#fff', textDecoration: 'underline'}} className="link-default" to={`/users/${userId}`}>{userEmail}</Link>: ' - '}
      </BadgeThin>
    </Paragraph>
  )
}

export default OrderSummariesColumn;

OrderSummariesColumn.defaultProps = {
  size: '-',
  SKU: '-',
  preOrder: false
}

OrderSummariesColumn.propTypes = {
  invoiceNumber: propTypes.string.isRequired,
  displayName: propTypes.string,
  SKU: propTypes.string,
  colour: propTypes.string,
  size: propTypes.string,
  userId: propTypes.number,
  userEmail: propTypes.string,
  paymentMethod: propTypes.string,
  paymentBankTransfer: propTypes.array,
  preOrder: propTypes.oneOfType([propTypes.number, propTypes.bool]),
  franchise: propTypes.string
}